import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useStripe, Elements, PaymentElement, useElements } from "@stripe/react-stripe-js";
import { getStripe } from "@/lib/stripeClient";
import { useLocation, useRoute, useSearch } from "wouter";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, ImagePlus, Loader2, Sprout, X } from "lucide-react";
import type { Item, User } from "@shared/schema";

if (!import.meta.env.VITE_STRIPE_PUBLIC_KEY) {
  throw new Error("Missing required Stripe key: VITE_STRIPE_PUBLIC_KEY");
}

type ItemDetail = Item & { seller?: User | null };

function ReserveForm({
  reservationId,
  onDone,
}: {
  reservationId: string;
  onDone: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setIsProcessing(true);
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: "if_required",
      confirmParams: {
        return_url: `${window.location.origin}/reservations`,
      },
    });
    if (error) {
      toast({
        title: "Payment failed",
        description: error.message ?? "Card was declined",
        variant: "destructive",
      });
      setIsProcessing(false);
      return;
    }
    if (paymentIntent?.status === "succeeded") {
      try {
        await fetch(`/api/reservations/${reservationId}/confirm-deposit`, {
          method: "POST",
          credentials: "include",
        });
      } catch {
        // The cron has the customer + payment method via the PI, so even
        // if confirm-deposit fails we'll resync on next view.
      }
      onDone();
    } else {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <PaymentElement />
      <Button
        type="submit"
        className="w-full"
        disabled={!stripe || !elements || isProcessing}
      >
        {isProcessing ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : null}
        Confirm reservation
      </Button>
    </form>
  );
}

export default function ReserveCheckout() {
  const stripePromise = useMemo(() => getStripe(), []);
  const [, params] = useRoute("/reserve/:itemId");
  const search = useSearch();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const itemId = params?.itemId ?? "";
  const queryParams = new URLSearchParams(search);
  const requestedQty = Math.max(1, Number(queryParams.get("qty") ?? "1") || 1);

  const [quantity, setQuantity] = useState(requestedQty);
  const [reservation, setReservation] = useState<{
    reservationId: string;
    clientSecret: string;
    depositCents: number;
    balanceCents: number;
    totalCents: number;
    isMeatShare?: boolean;
    estimatedRipeDate: string;
  } | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [requestText, setRequestText] = useState("");
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const [deliveryChoice, setDeliveryChoice] = useState<"pickup" | "delivery">("pickup");
  const [deliveryAddr, setDeliveryAddr] = useState({
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    zipCode: "",
  });
  const [addrPrefilled, setAddrPrefilled] = useState(false);

  type DeliveryAddr = typeof deliveryAddr;
  const { data: eligibility } = useQuery<{ eligible: boolean; address?: DeliveryAddr }>({
    queryKey: ["/api/items", itemId, "reservation-delivery-eligibility", requestedQty],
    queryFn: async () => {
      const res = await fetch(
        `/api/items/${itemId}/reservation-delivery-eligibility?qty=${requestedQty}`,
        { credentials: "include" },
      );
      if (!res.ok) return { eligible: false };
      return res.json();
    },
    enabled: Boolean(itemId),
  });
  const deliveryEligible = Boolean(eligibility?.eligible);

  useEffect(() => {
    if (!addrPrefilled && eligibility?.address) {
      setDeliveryAddr({
        addressLine1: eligibility.address.addressLine1 || "",
        addressLine2: eligibility.address.addressLine2 || "",
        city: eligibility.address.city || "",
        state: eligibility.address.state || "",
        zipCode: eligibility.address.zipCode || "",
      });
      setAddrPrefilled(true);
    }
  }, [eligibility, addrPrefilled]);

  const { data: item } = useQuery<ItemDetail>({
    queryKey: ["/api/items", itemId],
    queryFn: async () => {
      const res = await fetch(`/api/items/${itemId}`, { credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    enabled: Boolean(itemId),
  });

  const isMeatShare = Number((item as any)?.isMeatShare ?? 0) === 1;
  const processingFeeCents = Math.max(0, Number((item as any)?.processingFeeCents) || 0);

  const ripeDate = useMemo(() => {
    if (reservation?.estimatedRipeDate) return new Date(reservation.estimatedRipeDate);
    if (item && Number((item as any).isMeatShare ?? 0) === 1 && (item as any).slaughterDate) {
      return new Date((item as any).slaughterDate);
    }
    if (item && (item as any).estimatedRipeDate) {
      return new Date((item as any).estimatedRipeDate);
    }
    if (item && (item as any).isMadeToOrder === 1) {
      const lead = Number((item as any).leadTimeDays) || 3;
      return new Date(Date.now() + lead * 86_400_000);
    }
    return null;
  }, [reservation, item]);

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (imageUrls.length >= 6) {
      toast({ title: "Up to 6 photos", variant: "destructive" });
      return;
    }
    setUploadingImage(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const res = await fetch("/api/uploads/item-image", {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      if (!res.ok) throw new Error(await res.text());
      const { imageUrl } = (await res.json()) as { imageUrl: string };
      setImageUrls((prev) => [...prev, imageUrl]);
    } catch {
      toast({ title: "Couldn't upload that photo", description: "Try again.", variant: "destructive" });
    } finally {
      setUploadingImage(false);
    }
  };

  const handleCreate = async () => {
    setCreating(true);
    setError(null);
    try {
      const res = await fetch(`/api/items/${itemId}/reservations`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          quantity,
          requestText: requestText.trim() || undefined,
          requestImageUrls: imageUrls.length ? imageUrls : undefined,
          fulfillmentMethod:
            deliveryEligible && deliveryChoice === "delivery"
              ? "community_delivery"
              : "seller_pickup",
          deliveryAddress:
            deliveryEligible && deliveryChoice === "delivery" ? deliveryAddr : undefined,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setReservation(data);
    } catch (err: any) {
      const message = err?.message || "Could not start reservation";
      setError(message);
      toast({ title: "Reservation failed", description: message, variant: "destructive" });
    } finally {
      setCreating(false);
    }
  };

  const handleDone = () => {
    toast({
      title: "Reservation held!",
      description: isMeatShare
        ? "We'll charge the processing fee on the slaughter date."
        : "We'll auto-charge the balance on the ripe date.",
    });
    setLocation("/reservations");
  };

  if (!itemId) return null;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header cartCount={0} onCartClick={() => {}} />
      <main className="flex-1">
        <div className="container mx-auto max-w-xl px-4 py-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setLocation(`/items/${itemId}`)}
            className="mb-3"
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            Back
          </Button>

          <Card className="mb-4">
            <CardContent className="pt-5 space-y-3">
              <div className="flex items-center gap-2">
                <Sprout className="h-4 w-4 text-emerald-700" />
                <h1 className="text-lg font-semibold">
                  {!item
                    ? "Reserve"
                    : isMeatShare
                      ? "Reserve a share"
                      : (item as any)?.isMadeToOrder === 1
                        ? "Reserve your custom order"
                        : "Reserve on the vine"}
                </h1>
              </div>
              {item && (
                <div className="flex items-center gap-3">
                  {item.imageUrl && (
                    <img
                      src={item.imageUrl}
                      alt={item.name}
                      className="h-16 w-16 rounded-lg object-cover"
                    />
                  )}
                  <div>
                    <div className="font-semibold">{item.name}</div>
                    <div className="text-xs text-muted-foreground">
                      ${parseFloat(item.price).toFixed(2)} × {quantity}
                    </div>
                    {ripeDate && (
                      <div className="text-xs text-emerald-800">
                        {isMeatShare ? "Processing ~" : "Ready ~"}
                        {ripeDate.toLocaleDateString(undefined, {
                          weekday: "short",
                          month: "short",
                          day: "numeric",
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {!reservation && item && (
                <div className="space-y-2 pt-1">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-medium">Quantity</label>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                      >
                        −
                      </Button>
                      <span className="w-6 text-center font-semibold">{quantity}</span>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          setQuantity((q) => Math.min(item.qtyAvailable, q + 1))
                        }
                      >
                        +
                      </Button>
                    </div>
                  </div>

                  <div className="rounded-lg border bg-emerald-50/50 p-3 text-sm space-y-1">
                    {isMeatShare ? (
                      <>
                        <div className="flex justify-between">
                          <span>Share (today)</span>
                          <span className="font-semibold">
                            ${(parseFloat(item.price) * quantity).toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between text-muted-foreground">
                          <span>Processing fee (on slaughter date)</span>
                          <span>${((processingFeeCents * quantity) / 100).toFixed(2)}</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex justify-between">
                          <span>Deposit (today)</span>
                          <span className="font-semibold">
                            ${(parseFloat(item.price) * quantity * 0.25).toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between text-muted-foreground">
                          <span>
                            {(item as any)?.isMadeToOrder === 1
                              ? "Balance (when ready)"
                              : "Balance (on ripe)"}
                          </span>
                          <span>
                            ${(parseFloat(item.price) * quantity * 0.75).toFixed(2)}
                          </span>
                        </div>
                      </>
                    )}
                  </div>

                  <p className="text-[11px] text-emerald-900/70">
                    {isMeatShare
                      ? "You're buying a share of a live animal, custom-processed and marked Not for Sale for your household. Free cancel for 48 hours, full refund. After that the share price is non-refundable; the processing fee only charges on the slaughter date."
                      : "Free cancel for 48 hours after reserving — full refund. After that, cancelling forfeits the 25% deposit (the 75% balance never charges)."}
                  </p>

                  {(item as any).isMadeToOrder === 1 && (
                    <div>
                      <label className="mb-1 block text-xs font-medium">Your request</label>
                      <textarea
                        value={requestText}
                        onChange={(e) => setRequestText(e.target.value)}
                        rows={3}
                        placeholder="Tell the maker what you'd like — flavors, colors, design, occasion, date needed…"
                        className="w-full rounded-lg border bg-background px-3 py-2 text-sm leading-6 outline-none focus:border-primary"
                      />
                      <input
                        ref={fileRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleFileSelected}
                      />
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        {imageUrls.map((url) => (
                          <div key={url} className="relative h-16 w-16">
                            <img
                              src={url}
                              alt="Reference"
                              className="h-16 w-16 rounded-lg border object-cover"
                            />
                            <button
                              type="button"
                              onClick={() =>
                                setImageUrls((prev) => prev.filter((u) => u !== url))
                              }
                              className="absolute -right-1.5 -top-1.5 rounded-full bg-foreground/80 p-0.5 text-background"
                              aria-label="Remove photo"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                        {imageUrls.length < 6 && (
                          <button
                            type="button"
                            onClick={() => fileRef.current?.click()}
                            disabled={uploadingImage}
                            className="flex h-16 w-16 flex-col items-center justify-center gap-1 rounded-lg border border-dashed text-[10px] text-muted-foreground hover:border-primary hover:text-primary disabled:opacity-50"
                          >
                            {uploadingImage ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <>
                                <ImagePlus className="h-4 w-4" />
                                Add photo
                              </>
                            )}
                          </button>
                        )}
                      </div>
                      <p className="mt-1 text-[11px] text-muted-foreground">
                        Add example photos so the maker knows exactly what you want.
                      </p>
                    </div>
                  )}

                  {deliveryEligible && (
                    <div className="rounded-lg border bg-emerald-50/40 p-3">
                      <div className="mb-2 text-xs font-medium">How do you want it?</div>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => setDeliveryChoice("pickup")}
                          className={`rounded-lg border px-3 py-2 text-sm ${
                            deliveryChoice === "pickup"
                              ? "border-primary bg-primary/10 font-semibold"
                              : "bg-background"
                          }`}
                        >
                          Pickup
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeliveryChoice("delivery")}
                          className={`rounded-lg border px-3 py-2 text-sm ${
                            deliveryChoice === "delivery"
                              ? "border-primary bg-primary/10 font-semibold"
                              : "bg-background"
                          }`}
                        >
                          Free delivery
                        </button>
                      </div>
                      {deliveryChoice === "delivery" && (
                        <div className="mt-2 space-y-2">
                          <p className="text-[11px] text-emerald-900/70">
                            Free delivery from this founding maker. Where should it go?
                          </p>
                          <input
                            value={deliveryAddr.addressLine1}
                            onChange={(e) =>
                              setDeliveryAddr((a) => ({ ...a, addressLine1: e.target.value }))
                            }
                            placeholder="Street address"
                            className="w-full rounded-lg border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
                          />
                          <input
                            value={deliveryAddr.addressLine2}
                            onChange={(e) =>
                              setDeliveryAddr((a) => ({ ...a, addressLine2: e.target.value }))
                            }
                            placeholder="Apt, unit (optional)"
                            className="w-full rounded-lg border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
                          />
                          <div className="grid grid-cols-3 gap-2">
                            <input
                              value={deliveryAddr.city}
                              onChange={(e) =>
                                setDeliveryAddr((a) => ({ ...a, city: e.target.value }))
                              }
                              placeholder="City"
                              className="col-span-1 rounded-lg border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
                            />
                            <input
                              value={deliveryAddr.state}
                              onChange={(e) =>
                                setDeliveryAddr((a) => ({ ...a, state: e.target.value }))
                              }
                              placeholder="State"
                              className="col-span-1 rounded-lg border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
                            />
                            <input
                              value={deliveryAddr.zipCode}
                              onChange={(e) =>
                                setDeliveryAddr((a) => ({ ...a, zipCode: e.target.value }))
                              }
                              placeholder="ZIP"
                              className="col-span-1 rounded-lg border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <Button
                    onClick={handleCreate}
                    disabled={
                      creating ||
                      ((item as any).isMadeToOrder === 1 && !requestText.trim()) ||
                      (deliveryEligible &&
                        deliveryChoice === "delivery" &&
                        !(
                          deliveryAddr.addressLine1.trim() &&
                          deliveryAddr.city.trim() &&
                          deliveryAddr.state.trim() &&
                          deliveryAddr.zipCode.trim()
                        ))
                    }
                    className="w-full"
                  >
                    {creating ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : null}
                    {isMeatShare ? "Continue to payment" : "Continue to deposit"}
                  </Button>
                  {error && (
                    <p className="text-xs text-destructive">{error}</p>
                  )}
                </div>
              )}

              {reservation && (
                <div className="space-y-3 pt-2">
                  <div className="rounded-lg border bg-emerald-50/50 p-3 text-sm">
                    <div className="flex justify-between">
                      <span>
                        {reservation.isMeatShare ? "Share charged today" : "Deposit charged today"}
                      </span>
                      <span className="font-semibold">
                        ${(reservation.depositCents / 100).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-muted-foreground text-xs mt-0.5">
                      <span>
                        {reservation.isMeatShare
                          ? "Processing fee auto-charges on slaughter date"
                          : (item as any)?.isMadeToOrder === 1
                            ? "Balance auto-charges when ready"
                            : "Balance auto-charges on ripe date"}
                      </span>
                      <span>${(reservation.balanceCents / 100).toFixed(2)}</span>
                    </div>
                  </div>
                  <Elements
                    stripe={stripePromise}
                    options={{
                      clientSecret: reservation.clientSecret,
                      appearance: { theme: "stripe" },
                    }}
                  >
                    <ReserveForm
                      reservationId={reservation.reservationId}
                      onDone={handleDone}
                    />
                  </Elements>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
